import React from "react";
import clsx from "clsx";
import {
    Calendar,
    Check,
    ClipboardCheck,
    ClipboardList,
    FileText,
    Flag,
    type LucideIcon,
} from "lucide-react";
import { Card } from "../../../../shared/card/card.tsx";
import type { OffboardingStatus } from "../../schemas/index.ts";
import styles from "./resignation-progress-tracker.module.css";

interface ResignationProgressTrackerProps {
    status: OffboardingStatus;
    allOtherClearancesComplete?: boolean;
}

type StepState = "completed" | "current" | "upcoming";

type StepDefinition = {
    id: string;
    label: string;
    icon: LucideIcon;
};

const STEPS: StepDefinition[] = [
  { id: "submitted", label: "Offboarding Request Submitted", icon: FileText },
  { id: "under-review", label: "Under Review", icon: FileText },
  { id: "lwd-confirmed", label: "Last Working Day Confirmed", icon: Calendar },
  { id: "clearances", label: "Clearances In Progress", icon: ClipboardCheck },
  {
    id: "exit-interview",
    label: "Exit Interview Pending",
    icon: ClipboardList,
  },
  { id: "hr-clearance", label: "HR Clearance", icon: ClipboardCheck },
  { id: "exit-completed", label: "Exit Completed", icon: Flag },
];

function getCurrentStepIndex(
  status: OffboardingStatus,
  allOtherClearancesComplete = false,
): number {
  switch (status) {
    case "RESIGNATION_SUBMITTED":
    case "UNDER_REVIEW":
      return 1;
    case "LWD_CONFIRMED":
      return 2;
    case "CLEARANCE_IN_PROGRESS":
      if (allOtherClearancesComplete) return 4;
      return 3;
    case "EXIT_FORM_PENDING":
      return 4;
    case "HR_CLEARANCE_PENDING":
      return 5;
    case "EXIT_COMPLETED":
      return 6;
    default:
      return 1;
  }
}

function getStepState(stepIndex: number, currentIndex: number): StepState {
  if (stepIndex === 0) {
    return "completed";
  }

  if (currentIndex >= STEPS.length - 1) {
    return "completed";
  }

  if (stepIndex < currentIndex) {
    return "completed";
  }

  if (stepIndex === currentIndex) {
    return "current";
  }

  return "upcoming";
}

export function ResignationProgressTracker({
  status,
  allOtherClearancesComplete,
}: ResignationProgressTrackerProps) {
  const currentIndex = getCurrentStepIndex(status, allOtherClearancesComplete);

  return (
    <Card className={styles.card}>
      <h3 className={clsx("heading-sm", styles.title)}>Offboarding Progress</h3>

      <ol className={styles.list} aria-label="Offboarding progress">
        {STEPS.map((step, index) => {
          const stepState = getStepState(index, currentIndex);
          const StepIcon = step.icon;
          const segmentBeforeComplete =
            index > 0 && getStepState(index - 1, currentIndex) === "completed";
          const segmentAfterComplete = stepState === "completed";

          return (
            <li
              key={step.id}
              className={styles.item}
              aria-current={stepState === "current" ? "step" : undefined}
            >
              <div className={styles.trackRow}>
                <div
                  className={clsx(
                    styles.segment,
                    styles.segmentBefore,
                    segmentBeforeComplete && styles.segmentComplete,
                  )}
                  aria-hidden
                />
                <span
                  className={clsx(
                    styles.iconCircle,
                    stepState === "completed" && styles.iconCircleCompleted,
                    stepState === "current" && styles.iconCircleCurrent,
                    stepState === "upcoming" && styles.iconCircleUpcoming,
                  )}
                  aria-hidden
                >
                  {stepState === "completed" ? (
                    <Check size={18} strokeWidth={2.5} />
                  ) : (
                    <StepIcon size={17} strokeWidth={2} />
                  )}
                </span>
                <div
                  className={clsx(
                    styles.segment,
                    styles.segmentAfter,
                    segmentAfterComplete && styles.segmentComplete,
                  )}
                  aria-hidden
                />
              </div>
              <span
                className={clsx(
                  styles.label,
                  stepState === "current" && styles.labelCurrent,
                  stepState === "completed" && styles.labelCompleted,
                  stepState === "upcoming" && styles.labelUpcoming,
                )}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
